// src/components/common/PaymentRecordsModal/PaymentRecordsModal.jsx
//
// Channel-agnostic "Payment Details" modal extracted from OfflineSalesPaymentRecordsModal:
// existing payment records table (inline reference/date/proof edits, optional verification),
// an "add new payment" section (PaymentMethodPicker + payment-record cards), and an optional
// offset-note draft (credit note for offline sales, debit note for purchase orders).
// All data access goes through the `adapter` prop — see the wrappers:
//   src/pages/offlineSales/components/OfflineSalesPaymentRecordsModal.jsx
//   src/pages/purchaseOrder/components/PurchaseOrderPaymentRecordsModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, CheckCircle2, XCircle, Save, Upload, Paperclip, Trash2, Calendar, ShieldCheck,
  DollarSign, Ban,
} from 'lucide-react';
import DataTable from '../DataTable/DataTable';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import { formatDate, formatTime } from '../../../utils/dateUtils';
import { getImageUrl } from '../../../utils/imageUtils';
import { isBankTransferMissingRef } from '../../../utils/paymentRecordUtils';
import { PaymentMethodIcon } from '../../../utils/paymentMethodIcons';
import PaymentMethodPicker from '../../ui/PaymentMethodPicker/PaymentMethodPicker';
import Dropdown from '../../ui/Dropdown/Dropdown';
import { emptyCheque, getChequeErrorKey, toChequePayload } from '../ChequeDetailsFields/ChequeDetailsFields';
import PaymentRecordEntry from '../PaymentRecordEntry/PaymentRecordEntry';
import DatePicker from '../../ui/DatePicker/DatePicker';
import formStyles from '../../../pages/offlineSales/CreateOfflineSalesOrderPage.module.css';
import { getLocalTodayYMD, clampToToday } from '../../../utils/dateUtils';
import styles from '../../../pages/offlineSales/components/OfflineSalesPaymentRecordsModal.module.css';

const VALID_PROOF_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB
const isPdfUrl = (url) => !!url && url.toLowerCase().endsWith('.pdf');
const makeLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const PaymentRecordsModal = ({ entity, adapter, onClose, onSaved }) => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { texts, verification, offset } = adapter;
  const canVerify = !!verification?.canVerify;
  const canEditRecords = !!adapter.canEditRecords;
  const canApplyOffset = !!offset?.canApply;
  const canDeleteRecords = !!adapter.deleteRecord;
  const inputPrefix = adapter.inputIdPrefix || 'pr';

  const [paymentRecords, setPaymentRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editBuffers, setEditBuffers] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [proofErrors, setProofErrors] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(entity);

  // ─── New payment draft ───
  const [draftPayments, setDraftPayments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showChequeErrors, setShowChequeErrors] = useState(false);
  const [offsetNotes, setOffsetNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showOffsetDraft, setShowOffsetDraft] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState('');
  const [noteApplyAmount, setNoteApplyAmount] = useState('');

  useEffect(() => {
    setCurrentEntity(entity);
  }, [entity]);

  const balanceAmount = adapter.getBalance(currentEntity);
  const draftTotal = draftPayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const partyId = adapter.getPartyId(currentEntity);

  const fetchPaymentRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setPaymentRecords(await adapter.loadRecords(entity.id));
      setEditBuffers({});
    } catch (err) {
      console.error('Failed to fetch payment records:', err);
      showError(t('paymentVerification.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.id, showError, t]);

  useEffect(() => {
    fetchPaymentRecords();
    adapter.loadMethods()
      .then((data) => setPaymentMethods(Array.isArray(data) ? data : []))
      .catch(() => setPaymentMethods([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaymentRecords]);

  const loadOffsetNotes = useCallback(async () => {
    setOffsetNotes(await offset.loadNotes(partyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId]);

  useEffect(() => {
    if (!canApplyOffset || !partyId) {
      setOffsetNotes([]);
      setShowOffsetDraft(false);
      setSelectedNoteId('');
      setNoteApplyAmount('');
      return;
    }

    setIsLoadingNotes(true);
    loadOffsetNotes()
      .catch(() => setOffsetNotes([]))
      .finally(() => setIsLoadingNotes(false));
  }, [canApplyOffset, partyId, loadOffsetNotes]);

  const availableNotes = useMemo(() => (
    offsetNotes
      .map((note) => ({
        ...note,
        remainingAmount: Math.max(0, Number(note.amount || 0) - Number(note.appliedAmount || 0)),
      }))
      .filter((note) => note.status !== 'cancelled' && note.remainingAmount > 0)
  ), [offsetNotes]);

  const selectedNote = availableNotes.find((note) => note.id === Number(selectedNoteId));
  const balanceBeforeOffsetDraft = Math.max(0, balanceAmount - draftTotal);
  const offsetDraftAmount = showOffsetDraft && selectedNote
    ? Math.max(0, Number(noteApplyAmount || 0))
    : 0;
  const remainingBalance = Math.max(0, balanceBeforeOffsetDraft - offsetDraftAmount);
  const pickerMethods = canApplyOffset && remainingBalance > 0
    ? [...paymentMethods, { key: offset.key, label: offset.label, iconFileName: offset.iconFileName }]
    : paymentMethods;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ─── Existing record edit/verify ───
  const isVoided = (record) => record.status === 'void';
  const isRecordLocked = (record) => isVoided(record) || record.isVerified || record.readOnly || !canEditRecords;

  const getFieldValue = (record, field) => {
    if (editBuffers[record.id] && editBuffers[record.id][field] !== undefined) {
      return editBuffers[record.id][field];
    }
    return record[field] ?? '';
  };

  const isDirty = (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return false;
    return (
      (buffer.reference !== undefined && buffer.reference !== (record.reference || '')) ||
      (buffer.paymentDate !== undefined && buffer.paymentDate !== (record.paymentDate || ''))
    );
  };

  const handleFieldChange = (recordId, field, value) => {
    setEditBuffers((prev) => ({ ...prev, [recordId]: { ...prev[recordId], [field]: value } }));
  };

  const replaceRecordInState = (updatedRecord) => {
    setPaymentRecords((prev) => prev.map((r) => (r.id === updatedRecord.id ? { ...r, ...updatedRecord } : r)));
  };

  const handleSaveEdit = async (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return;
    try {
      const payload = {};
      if (buffer.reference !== undefined) payload.reference = buffer.reference;
      if (buffer.paymentDate !== undefined) payload.paymentDate = buffer.paymentDate;
      replaceRecordInState(await adapter.updateRecord(record.id, payload));
      setEditBuffers((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to update payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const handleProofChange = async (record, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!VALID_PROOF_TYPES.includes(file.type)) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.invalidProofFormat') || 'Only .jpg, .png or .pdf files are allowed' }));
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.proofTooLarge') || 'File must be under 5MB' }));
      return;
    }

    setProofErrors((prev) => ({ ...prev, [record.id]: '' }));
    setUploadingId(record.id);
    try {
      const proofUrl = await adapter.uploadProof(file);
      replaceRecordInState(await adapter.updateRecord(record.id, { proofUrl }));
    } catch (err) {
      console.error('Payment proof upload failed:', err);
      setProofErrors((prev) => ({ ...prev, [record.id]: t('paymentVerification.proofUploadFailed') }));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteProof = async (record) => {
    const confirmed = await confirm(t('paymentVerification.confirmDeleteProof'), {
      title: t('paymentVerification.deleteProof'),
      confirmText: t('paymentVerification.deleteProof'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;
    try {
      replaceRecordInState(await adapter.updateRecord(record.id, { proofUrl: null }));
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to delete payment proof:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const handleDeleteRecord = async (record) => {
    const confirmed = await confirm(t('ecomOrders.confirmDeletePaymentRecord') || 'Delete this payment record?', {
      title: t('common.delete'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;
    try {
      await adapter.deleteRecord(record);
      await fetchPaymentRecords();
      setCurrentEntity(await adapter.reloadEntity(entity.id));
      success(t('paymentVerification.updateSuccess'));
      onSaved?.();
    } catch (err) {
      console.error('Failed to delete payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const getVerifyBlockReason = (record) => {
    if (isDirty(record)) return t('paymentVerification.saveChangesBeforeVerify');
    if (isBankTransferMissingRef(record)) return t('paymentVerification.referenceRequiredForBankTransfer');
    return null;
  };

  const handleVerifyOne = async (record) => {
    const blockReason = getVerifyBlockReason(record);
    if (blockReason) {
      showError(blockReason);
      return;
    }

    const confirmed = await confirm(t('paymentVerification.confirmVerify'), {
      title: t('paymentVerification.verify'),
      confirmText: t('paymentVerification.verify'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;

    try {
      replaceRecordInState(await verification.verifyRecord(record.id));
      setSelectedIds((prev) => prev.filter((id) => id !== record.id));
      success(t('paymentVerification.verifySuccess'));
      onSaved?.();
    } catch (err) {
      console.error('Failed to verify payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.verifyFailed'));
    }
  };

  const unverifiedSelectedIds = selectedIds.filter((id) => {
    const record = paymentRecords.find((r) => r.id === id);
    return record && !record.isVerified;
  });

  const handleBulkVerify = async () => {
    if (unverifiedSelectedIds.length === 0) {
      showError(t('paymentVerification.noUnverifiedSelected'));
      return;
    }

    const unsavedCount = unverifiedSelectedIds.filter((id) =>
      isDirty(paymentRecords.find((r) => r.id === id))
    ).length;
    if (unsavedCount > 0) {
      showError(t('paymentVerification.bulkUnsavedChanges', { count: unsavedCount }));
      return;
    }

    const missingRefCount = unverifiedSelectedIds.filter((id) =>
      isBankTransferMissingRef(paymentRecords.find((r) => r.id === id))
    ).length;
    if (missingRefCount > 0) {
      showError(t('paymentVerification.bulkReferenceMissing', { count: missingRefCount }));
      return;
    }
    const confirmed = await confirm(
      t('paymentVerification.confirmBulkVerify', { count: unverifiedSelectedIds.length }),
      {
        title: t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length }),
        confirmText: t('paymentVerification.verify'),
        cancelText: t('common.cancel'),
      }
    );
    if (!confirmed) return;

    try {
      const result = await verification.bulkVerify(unverifiedSelectedIds);
      setSelectedIds([]);
      await fetchPaymentRecords();
      onSaved?.();
      if (result.summary.failureCount === 0) {
        success(t('paymentVerification.bulkVerifySuccess', { count: result.summary.successCount }));
      } else {
        showError(
          t('paymentVerification.bulkVerifyPartial', {
            successCount: result.summary.successCount,
            totalSubmitted: result.summary.totalSubmitted,
            failureCount: result.summary.failureCount,
          })
        );
      }
    } catch (err) {
      console.error('Bulk verify failed:', err);
      showError(err.response?.data?.message || t('paymentVerification.bulkVerifyFailed'));
    }
  };

  // ─── New payment draft helpers ───
  const addDraftPayment = (paymentMethodKey = '') => {
    setDraftPayments((prev) => [
      ...prev,
      {
        id: makeLocalId(),
        paymentMethodKey,
        amount: remainingBalance > 0 ? remainingBalance : '',
        paymentDate: getLocalTodayYMD(),
        reference: '',
        proofUrl: null,
        cheque: emptyCheque(),
      },
    ]);
  };

  const isChequeDraft = (p) => Boolean(adapter.enableChequeDetails) && p.paymentMethodKey === 'cheque';

  const updateDraftPayment = (id, field, value) => {
    setDraftPayments((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        if (field === 'amount') return { ...p, amount: parseFloat(value) || 0 };
        return { ...p, [field]: value };
      })
    );
  };

  const removeDraftPayment = (id) => {
    setDraftPayments((prev) => prev.filter((p) => p.id !== id));
  };

  const handlePaymentMethodSelect = (key) => {
    if (offset && key === offset.key) {
      setShowOffsetDraft(true);
      setSelectedNoteId('');
      setNoteApplyAmount('');
      return;
    }
    addDraftPayment(key);
  };

  const handleNoteSelect = (value) => {
    const note = availableNotes.find((n) => n.id === Number(value));
    setSelectedNoteId(value || '');
    setNoteApplyAmount(note ? String(Math.min(note.remainingAmount, balanceBeforeOffsetDraft)) : '');
  };

  const clearOffsetDraft = () => {
    setShowOffsetDraft(false);
    setSelectedNoteId('');
    setNoteApplyAmount('');
  };

  const handleSaveDraftPayments = async () => {
    const hasOffsetDraft = showOffsetDraft || selectedNoteId || noteApplyAmount;
    if (draftPayments.length === 0 && !hasOffsetDraft) return;

    const invalid = draftPayments.some((p) => !p.paymentMethodKey || !p.amount || parseFloat(p.amount) <= 0);
    if (invalid) {
      showError(texts.someFieldsRequired);
      return;
    }
    const draftError = adapter.validateDrafts?.(draftPayments);
    if (draftError) {
      showError(draftError);
      return;
    }
    const chequeErrorKey = draftPayments.filter(isChequeDraft).map((p) => getChequeErrorKey(p.cheque)).find(Boolean);
    if (chequeErrorKey) {
      setShowChequeErrors(true);
      showError(t(chequeErrorKey));
      return;
    }

    const normalDraftTotal = draftPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const balanceAfterNormalDrafts = Math.max(0, balanceAmount - normalDraftTotal);
    const noteAmount = Number(noteApplyAmount);

    if (hasOffsetDraft) {
      if (!selectedNote) {
        showError(offset.texts.select);
        return;
      }
      if (!noteAmount || noteAmount <= 0) {
        showError(offset.texts.amountMustBePositive);
        return;
      }
      if (noteAmount > selectedNote.remainingAmount) {
        showError(offset.texts.exceedsRemaining);
        return;
      }
      if (noteAmount > balanceAfterNormalDrafts) {
        showError(offset.texts.exceedsBalance);
        return;
      }
    }

    setIsSaving(true);
    try {
      if (draftPayments.length > 0) {
        await adapter.recordPayments(
          entity.id,
          draftPayments.map((p) => ({
            paymentMethodKey: p.paymentMethodKey,
            amount: parseFloat(p.amount) || 0,
            paymentDate: p.paymentDate || null,
            reference: p.reference || null,
            proofUrl: p.proofUrl || null,
            ...(isChequeDraft(p) ? { cheque: toChequePayload(p.cheque) } : {}),
          }))
        );
      }
      if (hasOffsetDraft) {
        await offset.applyNote(selectedNote.id, entity.id, noteAmount);
      }
      success(texts.paymentRecordedSuccess);
      setDraftPayments([]);
      setShowChequeErrors(false);
      clearOffsetDraft();
      await fetchPaymentRecords();
      setCurrentEntity(await adapter.reloadEntity(entity.id));
      if (partyId && canApplyOffset) await loadOffsetNotes();
      onSaved?.();
    } catch (err) {
      showError(err?.response?.data?.message || texts.failedToRecordPayment);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = [
    {
      key: 'paymentMethod',
      header: t('paymentVerification.paymentMethod'),
      render: (record) => (
        <div className={styles.methodCell}>
          <PaymentMethodIcon iconFileName={record.paymentMethod?.iconFileName} label={record.paymentMethod?.label} />
        </div>
      ),
    },
    {
      key: 'paymentAmount',
      header: t('paymentVerification.amount'),
      align: 'right',
      width: '110px',
      render: (record) => (isVoided(record) ? (
        <div className={styles.voidAmountCell}>
          <span className={styles.voidAmount}>{formatCurrency(record.paymentAmount)}</span>
          <span
            className={`${styles.statusBadge} ${styles.voidBadge}`}
            title={record.voidedAt ? `${t('paymentVerification.voidedDate')}: ${formatDate(record.voidedAt)}` : undefined}
          >
            <Ban size={12} />
            {t('paymentVerification.void')}
          </span>
        </div>
      ) : formatCurrency(record.paymentAmount)),
    },
    {
      key: 'reference',
      header: t('paymentVerification.reference'),
      width: '220px',
      render: (record) => (
        <input
          type="text"
          className={styles.inlineInput}
          value={getFieldValue(record, 'reference')}
          disabled={isRecordLocked(record)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFieldChange(record.id, 'reference', e.target.value)}
        />
      ),
    },
    {
      key: 'paymentDate',
      header: t('paymentVerification.paymentDate'),
      width: '150px',
      render: (record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={getFieldValue(record, 'paymentDate') || ''}
            onChange={(value) => handleFieldChange(record.id, 'paymentDate', clampToToday(value))}
            maxDate={getLocalTodayYMD()}
            disabled={isRecordLocked(record)}
            placeholder={t('paymentVerification.paymentDate')}
            size="compact"
          />
        </div>
      ),
    },
    {
      key: 'proof',
      header: t('paymentVerification.proof'),
      width: '160px',
      render: (record) => (
        <div className={styles.proofCell} onClick={(e) => e.stopPropagation()}>
          {record.proofUrl ? (
            <>
              {isPdfUrl(record.proofUrl) ? (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
                  <Paperclip size={14} />
                  {t('paymentVerification.viewProof')}
                </a>
              ) : (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
                  <img src={getImageUrl(record.proofUrl)} alt="Payment proof" className={styles.proofThumbnail} />
                </a>
              )}
              {!isRecordLocked(record) && (
                <div className={styles.proofActions}>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.changeProof')}
                    onClick={() => document.getElementById(`${inputPrefix}ProofInput-${record.id}`).click()}
                  >
                    <Upload size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.deleteProof')}
                    onClick={() => handleDeleteProof(record)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </>
          ) : isRecordLocked(record) ? (
            <span className={styles.noProofText}>{t('paymentVerification.noFileChosen')}</span>
          ) : (
            <button
              type="button"
              className={styles.uploadProofButton}
              onClick={() => document.getElementById(`${inputPrefix}ProofInput-${record.id}`).click()}
              disabled={uploadingId === record.id}
            >
              <Upload size={14} />
              {uploadingId === record.id ? (t('common.uploading') || 'Uploading...') : t('paymentVerification.noFileChosen')}
            </button>
          )}
          {!isRecordLocked(record) && (
            <input
              id={`${inputPrefix}ProofInput-${record.id}`}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleProofChange(record, e)}
            />
          )}
          {proofErrors[record.id] && <span className={styles.errorText}>{proofErrors[record.id]}</span>}
        </div>
      ),
    },
    ...(verification ? [{
      key: 'isVerified',
      header: t('paymentVerification.isVerified'),
      width: '130px',
      render: (record) =>
        record.isVerified ? (
          <span className={`${styles.statusBadge} ${styles.verifiedBadge}`}>
            <CheckCircle2 size={14} />
            {t('paymentVerification.verified')}
          </span>
        ) : (
          <span className={`${styles.statusBadge} ${styles.unverifiedBadge}`}>
            <XCircle size={14} />
            {t('paymentVerification.unverified')}
          </span>
        ),
    }] : []),
    {
      key: 'createdAt',
      header: t('paymentVerification.createdDate'),
      width: '130px',
      render: (record) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(record.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = (canVerify || canDeleteRecords || (!verification && canEditRecords)) ? {
    width: '90px',
    frozen: true,
    render: (record) =>
      isRecordLocked(record) ? (
        <span className={styles.noActionText}>-</span>
      ) : (
        <div className={styles.actionsWrapper}>
          {isDirty(record) && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.saveButton}`}
              title={t('paymentVerification.save')}
              onClick={() => handleSaveEdit(record)}
            >
              <Save size={14} />
            </button>
          )}
          {canDeleteRecords && (
            <button
              type="button"
              className={styles.actionButton}
              title={t('common.delete')}
              onClick={() => handleDeleteRecord(record)}
            >
              <Trash2 size={14} />
            </button>
          )}
          {canVerify && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.verifyButton}`}
              title={getVerifyBlockReason(record) || t('paymentVerification.verify')}
              disabled={!!getVerifyBlockReason(record)}
              onClick={() => handleVerifyOne(record)}
            >
              <CheckCircle2 size={14} />
            </button>
          )}
        </div>
      ),
  } : undefined;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <ShieldCheck size={24} />
              <div>
                <h2>{texts.title}</h2>
                <p className={styles.orderNumber}>#{adapter.getNumber(entity)}</p>
              </div>
            </div>
            <button onClick={onClose} className={styles.closeButton}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          {adapter.renderExtraSection?.(currentEntity)}
          {adapter.renderPaymentType?.(currentEntity, paymentRecords)}
          {balanceAmount > 0 && (
            <div className={styles.newPaymentSection}>
              <div className={styles.newPaymentHeader}>
                <span>{texts.outstandingBalance}: <strong>{formatCurrency(remainingBalance)}</strong></span>
              </div>
              {adapter.disableNewPayments && adapter.disableNewPaymentsReason && (
                <p className={styles.noProofText}>{adapter.disableNewPaymentsReason}</p>
              )}
              <PaymentMethodPicker
                methods={pickerMethods}
                onSelect={handlePaymentMethodSelect}
                disabled={remainingBalance <= 0 || !!adapter.disableNewPayments}
                getTitle={(method) => adapter.disableNewPayments
                  ? adapter.disableNewPaymentsReason
                  : remainingBalance <= 0
                    ? (t('ecomOrders.noRemainingBalance') || 'No remaining balance to add a payment record')
                    : method.label}
              />
              {showOffsetDraft && (
                <div className={formStyles.paymentRecord}>
                  <div className={formStyles.paymentRecordHeader}>
                    <span className={formStyles.recordTitle}>
                      <PaymentMethodIcon iconFileName={offset.iconFileName} label={offset.label} />
                      {offset.label}
                    </span>
                    <button type="button" className={formStyles.removeItemBtn} onClick={clearOffsetDraft}>
                      <X size={15} />
                    </button>
                  </div>
                  <div className={formStyles.paymentRecordFields}>
                    <div className={formStyles.paymentRecordFieldRow}>
                      <div className={formStyles.formGroup}>
                        <label>{offset.texts.select} *</label>
                        <Dropdown
                          className={styles.creditNoteDropdown}
                          value={selectedNoteId}
                          onChange={handleNoteSelect}
                          options={availableNotes.map((note) => ({
                            value: note.id,
                            label: `${note[offset.numberField]} (${formatCurrency(note.remainingAmount)})`,
                          }))}
                          placeholder={isLoadingNotes ? (t('common.loading') || 'Loading...') : offset.texts.select}
                          searchable
                          searchPlaceholder={offset.texts.search}
                          disabled={isLoadingNotes || availableNotes.length === 0}
                        />
                        {!isLoadingNotes && availableNotes.length === 0 && (
                          <span className={formStyles.errorText}>{offset.texts.none}</span>
                        )}
                      </div>
                      <div className={formStyles.formGroup}>
                        <label>{offset.texts.applyAmount} *</label>
                        <div className={formStyles.inputWrapper}>
                          <DollarSign className={formStyles.inputIcon} size={16} />
                          <input
                            className={styles.creditNoteAmountInput}
                            type="number"
                            min="0.01"
                            step="0.01"
                            max={selectedNote ? Math.min(selectedNote.remainingAmount, balanceBeforeOffsetDraft) : balanceBeforeOffsetDraft}
                            placeholder="0.00"
                            value={noteApplyAmount}
                            onChange={(e) => setNoteApplyAmount(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {draftPayments.map((p, index) => (
                <PaymentRecordEntry
                  key={p.id}
                  index={index}
                  payment={p}
                  methods={paymentMethods}
                  title={texts.title}
                  amountLabel={texts.amount}
                  referenceLabel={texts.reference}
                  onChange={(field, value) => updateDraftPayment(p.id, field, value)}
                  onRemove={() => removeDraftPayment(p.id)}
                  onUploadProof={adapter.uploadProof}
                  maxAmount={balanceAmount}
                  enableCheque={Boolean(adapter.enableChequeDetails)}
                  showChequeErrors={showChequeErrors}
                  idPrefix={`${inputPrefix}Draft-${p.id}`}
                />
              ))}
              {(draftPayments.length > 0 || showOffsetDraft) && (
                <button type="button" className={styles.savePaymentsButton} onClick={handleSaveDraftPayments} disabled={isSaving}>
                  {isSaving ? (t('common.saving') || 'Saving...') : (
                    <>
                      <Save size={15} /> {texts.confirmPayment}
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {canVerify && selectedIds.length > 0 && (
            <div className={styles.bulkActionsBar}>
              <span className={styles.selectedCount}>{selectedIds.length} {t('common.selected') || 'selected'}</span>
              <div className={styles.bulkActionsRight}>
                <button
                  onClick={handleBulkVerify}
                  className={styles.bulkActionButton}
                  disabled={unverifiedSelectedIds.length === 0}
                >
                  <CheckCircle2 size={15} />
                  {t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length })}
                </button>
                <button onClick={() => setSelectedIds([])} className={styles.bulkCancelButton}>
                  {t('paymentVerification.clearSelection')}
                </button>
              </div>
            </div>
          )}

          <div className={styles.existingPaymentsSection}>
            <DataTable
              data={paymentRecords}
              columns={columns}
              rowClassName={(record) => (isVoided(record) ? styles.voidRow : '')}
              actions={tableActions}
              selectable={canVerify}
              selectedRows={selectedIds}
              onSelectionChange={setSelectedIds}
              loading={isLoading}
              emptyMessage={t('paymentVerification.noRecordsFound')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRecordsModal;
